import type { DriveStep } from 'driver.js';

export type NewPracticeWalkthroughFlags = {
  canPracticeScheduling: boolean;
  canMultiPersona: boolean;
  canMultiUser: boolean;
};

function el(selector: string): Element {
  return (document.querySelector(selector) as Element) ?? document.body;
}

/**
 * Pure step list for the new-practice page walkthrough. Element resolvers run when each step is shown.
 */
export function buildNewPracticeWalkthroughSteps(flags: NewPracticeWalkthroughFlags): DriveStep[] {
  const { canPracticeScheduling, canMultiPersona, canMultiUser } = flags;

  const steps: DriveStep[] = [
    {
      element: () => el('[data-tour="np-intro"]'),
      popover: {
        title: 'Configure your practice',
        description:
          'Pick how you want to practice, who you are selling to, and how the session should run. You can revisit this walkthrough anytime from Profile.',
        side: 'bottom',
        align: 'start',
      },
    },
  ];

  if (canPracticeScheduling) {
    steps.push({
      element: () => el('[data-tour="np-when"]'),
      popover: {
        title: 'Start now or schedule',
        description:
          'Start immediately or choose a time window for your team. Scheduling requires the practice scheduling feature on your plan.',
        side: 'bottom',
        align: 'start',
      },
    });
  }

  steps.push(
    {
      element: () => el('[data-tour="np-personas"]'),
      popover: {
        title: 'Personas',
        description: canMultiPersona
          ? 'Select one buyer or up to five for a buying committee. Use the filter chips to narrow by role type.'
          : 'Choose the buyer persona for this run. Use the filter chips to narrow by role type.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: () => el('[data-tour="np-time-limit"]'),
      popover: {
        title: 'Time limit',
        description:
          'Sessions end automatically at this duration. You can save a default in Profile for new practices.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: () => el('[data-tour="np-goals"]'),
      popover: {
        title: 'Goals (optional)',
        description:
          'Set a goal and/or pitch style. Switch between tabs. Goals are optional but help tailor coaching and evaluation.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: () => el('[data-tour="np-mode"]'),
      popover: {
        title: 'Advanced options',
        description:
          'Expand to choose Video Call (voice and optional camera) or Chat Practice (typing-only). Video Call is the default.',
        side: 'bottom',
        align: 'start',
      },
    },
  );

  if (canMultiUser) {
    steps.push({
      element: () => el('[data-tour="np-teammates"]'),
      popover: {
        title: 'Teammates and invites',
        description:
          'Allow organization members to join as co-presenters, optionally email calendar invites when you start or schedule.',
        side: 'top',
        align: 'start',
      },
    });
  }

  if (canPracticeScheduling) {
    steps.push({
      element: () => el('[data-tour="np-schedule-details"]'),
      popover: {
        title: 'Schedule details',
        description:
          'When “Schedule for later” is selected, set title and start/end times here before creating the schedule.',
        side: 'top',
        align: 'start',
      },
    });
  }

  steps.push({
    element: () => el('[data-tour="np-cta"]'),
    popover: {
      title: 'Start or create',
      description:
        'When everything looks right, start practice now or create the scheduled session. Fix any highlighted sections first if something is missing.',
      side: 'top',
      align: 'center',
    },
  });

  return steps;
}
